import { createFileRoute } from '@tanstack/react-router';
import { fallback, zodValidator } from '@tanstack/zod-adapter';
import z from 'zod';

import { BreadcrumbItemProps } from '@/features/admin/components/AdminPageHeader';
import { getUsersDefaultParams } from '@/features/manage-users/actions/getUsers';
import { UsersListRoot } from '@/features/manage-users/components/UsersListRoot';
import { getUsersQueryOptions } from '@/features/manage-users/queries/getUsers';
import { AppPageContent } from '@/features/shared/components/AppPageContent';
import { AppPageHeader } from '@/features/shared/components/AppPageHeader';
import { UserRoles } from '@/lib/constants/auth.constants.ts';
import { requireRole } from '@/lib/validation';

const usersSearchParams = z.object({
  search: fallback(z.string(), ''),
  page: fallback(z.number(), 0),
  perPage: fallback(z.number(), getUsersDefaultParams.limit),
  sort: fallback(z.string(), getUsersDefaultParams.sort),
  orderBy: fallback(z.string(), getUsersDefaultParams.orderBy),
  organizationId: fallback(z.string(), ''),
  role: fallback(z.string(), ''),
  alphaLevel: fallback(z.string(), ''),
});

export const Route = createFileRoute('/_app/app/admin-section/users/')({
  component: RouteComponent,
  beforeLoad: requireRole([UserRoles.ADMINISTRATOR]),
  validateSearch: zodValidator(usersSearchParams),
  loaderDeps: ({ search }) => ({
    search,
  }),
  loader: async ({ context, deps }) => {
    const { search } = deps;
    await context.queryClient.prefetchQuery(
      getUsersQueryOptions({
        offset: search.page * search.perPage,
        limit: search.perPage,
        sort: search.sort,
        orderBy: search.orderBy,
        search: search.search,
        organizationId: search.organizationId || undefined,
        role: search.role || undefined,
        alphaLevel: search.alphaLevel || undefined,
      })
    );
  },
  ssr: false,
});

const breadcrumbItems: BreadcrumbItemProps[] = [
  {
    label: 'Home',
    link: { to: '/app' },
  },
  {
    label: 'Administration',
    link: { to: '/app' },
  },
  {
    label: 'Users',
    link: { to: '/app/admin-section/users' },
  },
];

function RouteComponent() {
  return (
    <AppPageContent className="overflow-hidden h-[calc(100%-16px)] flex flex-col">
      <AppPageHeader breadcrumbItems={breadcrumbItems} />
      <UsersListRoot />
    </AppPageContent>
  );
}
